/**
 * Planning Header Component
 * Contains title, controls, and arrival lead time settings
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Radio, Trash2 } from 'lucide-react';
import { MatchdayPlanning } from '../../types/matchdayPlanning';
import { generatePlanningPDF } from '../../utils/pdfGenerator';

interface PlanningHeaderProps {
  planning: MatchdayPlanning;
  arrivalLeadTime: number;
  onArrivalLeadTimeChange: (value: number) => void;
  onSave: () => void;
  onAddSegment: () => void;
  onDeletePlanning?: () => void;
  isReadOnly?: boolean;
  showDrivingTimes?: boolean;
  onShowDrivingTimesChange?: (value: boolean) => void;
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}

const PlanningHeader: React.FC<PlanningHeaderProps> = ({
  planning,
  arrivalLeadTime,
  onArrivalLeadTimeChange,
  onSave,
  onAddSegment,
  onDeletePlanning,
  isReadOnly = false,
  showDrivingTimes = false,
  onShowDrivingTimesChange,
  onNavigatePrev,
  onNavigateNext,
  hasPrev = false,
  hasNext = false
}) => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const handleArrivalLeadTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 15 && value <= 300) {
      onArrivalLeadTimeChange(value);
    }
  };

  const handleDashboardClick = () => {
    navigate(isReadOnly ? '/partner/matchday-planning' : '/upcoming-matches');
  };

  const handleGeneratePDF = () => {
    generatePlanningPDF(planning);
  };

  // Format date for display (only date, no time)
  const formatDate = (dateString: string): string => {
    try {
      // Parse YYYY-MM-DD format as local date to avoid timezone shifts
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('de-DE', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Format kickoff time for display (HH:mm format)
  const formatKickoffTime = (timeString: string): string => {
    try {
      // If it's already in HH:mm format, return it
      if (/^\d{2}:\d{2}$/.test(timeString)) {
        return timeString;
      }
      // If it's in HH:mm:ss format, remove seconds
      if (/^\d{2}:\d{2}:\d{2}$/.test(timeString)) {
        return timeString.substring(0, 5);
      }
      return timeString;
    } catch {
      return timeString;
    }
  };

  return (
    <div className="planning-header">
      {/* Row 1: Arrows + Buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
        {(onNavigatePrev || onNavigateNext) && (
          <>
            <button
              type="button"
              className="btn btn-nav"
              onClick={onNavigatePrev}
              disabled={!hasPrev}
              title="Vorheriges Spiel"
              style={{
                padding: '4px 10px',
                fontSize: '1.1rem',
                lineHeight: 1,
                opacity: hasPrev ? 1 : 0.3,
                cursor: hasPrev ? 'pointer' : 'default',
                border: '1px solid #ccc',
                borderRadius: '6px',
                background: 'transparent'
              }}
            >
              ◀
            </button>
            <button
              type="button"
              className="btn btn-nav"
              onClick={onNavigateNext}
              disabled={!hasNext}
              title="Nächstes Spiel"
              style={{
                padding: '4px 10px',
                fontSize: '1.1rem',
                lineHeight: 1,
                opacity: hasNext ? 1 : 0.3,
                cursor: hasNext ? 'pointer' : 'default',
                border: '1px solid #ccc',
                borderRadius: '6px',
                background: 'transparent'
              }}
            >
              ▶
            </button>
          </>
        )}

        <button
          type="button"
          className={`btn planning-status-btn status-${planning.status}`}
          title="Status (Workflow kommt später)"
          style={{ cursor: 'default', padding: '4px 10px', fontSize: '0.8rem' }}
        >
          {getStatusText(planning.status)}
        </button>

        <button
          type="button"
          className="btn"
          style={{ backgroundColor: '#6f42c1', color: 'white', border: 'none', padding: '4px 10px', fontSize: '0.8rem' }}
          onClick={() => navigate(`/tracking?planningId=${planning.id}`)}
          title="Tracking starten"
        >
          <Radio size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Tracking
        </button>

        <button type="button" className="btn btn-secondary" onClick={handleDashboardClick} style={{ padding: '4px 10px', fontSize: '0.8rem' }}>
          {t('common:planning.dashboard')}
        </button>

        {!isReadOnly && (
          <>
            <button type="button" className="btn btn-primary" onClick={onAddSegment} style={{ padding: '4px 10px', fontSize: '0.8rem' }}>
              + {t('common:planning.addSegment')}
            </button>

            <button type="button" className="btn btn-success" onClick={onSave} style={{ padding: '4px 10px', fontSize: '0.8rem' }}>
              {t('common:save')}
            </button>
          </>
        )}

        <button type="button" className="btn btn-outline-danger" onClick={handleGeneratePDF} style={{ padding: '4px 10px', fontSize: '0.8rem' }}>
          PDF
        </button>

        {!isReadOnly && onDeletePlanning && (
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => {
              if (window.confirm('Planung wirklich löschen? Alle Segmente, Fahrzeug- und Personalzuweisungen werden unwiderruflich entfernt.')) {
                onDeletePlanning();
              }
            }}
            title="Planung löschen"
            style={{ padding: '4px 10px', fontSize: '0.8rem' }}
          >
            <Trash2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Löschen
          </button>
        )}
      </div>

      {/* Row 2: Filter left, Settings right */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'nowrap', marginTop: '8px', gap: '8px', whiteSpace: 'nowrap' }}>
        {/* Left: Info Bubbles */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
          <span className="planning-date" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
            {planning.name.replace(/^Planung:\s*/i, '')}
          </span>
          {planning.match?.matchday?.competition && (
            <span className="planning-date" style={{ fontSize: '0.85rem' }}>
              {planning.match.matchday.competition.shortName || planning.match.matchday.competition.name}
            </span>
          )}
          {planning.match?.matchday && (
            <span className="planning-date" style={{ fontSize: '0.85rem' }}>
              Spieltag {planning.match.matchday.number}
            </span>
          )}
          <span className="planning-date" style={{ fontSize: '0.85rem' }}>{formatDate(planning.date)}</span>
          <span className="planning-kickoff" style={{ fontSize: '0.85rem' }}>Anstoß: {formatKickoffTime(planning.kickoffTime)}</span>
        </div>

        {/* Right: Settings */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
          {!isReadOnly && (
            <span className="planning-kickoff" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem' }}>
              Vorlauf:
              <input
                type="number"
                value={arrivalLeadTime}
                min="15"
                max="300"
                onChange={handleArrivalLeadTimeChange}
                style={{
                  width: '52px',
                  padding: '1px 4px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  textAlign: 'center',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  background: 'white'
                }}
              />
              Min.
            </span>
          )}
          {onShowDrivingTimesChange && (
            <div
              onClick={() => onShowDrivingTimesChange(!showDrivingTimes)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                userSelect: 'none',
                whiteSpace: 'nowrap',
                fontSize: '0.85rem',
                fontWeight: 500,
                color: '#495057'
              }}
            >
              <div style={{
                width: '40px',
                height: '22px',
                borderRadius: '11px',
                background: showDrivingTimes ? '#4CAF50' : '#ccc',
                position: 'relative',
                transition: 'background 0.2s ease',
                flexShrink: 0
              }}>
                <div style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: 'white',
                  position: 'absolute',
                  top: '2px',
                  left: showDrivingTimes ? '20px' : '2px',
                  transition: 'left 0.2s ease',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} />
              </div>
              Lenkzeiten
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Get status text based on planning status
 */
const getStatusText = (status: string): string => {
  switch (status) {
    case 'draft': return 'Entwurf';
    case 'confirmed': return 'Bestätigt';
    case 'inprogress': return 'Unterwegs';
    case 'completed': return 'Abgeschlossen';
    case 'cancelled': return 'Abgebrochen';
    default: return 'Entwurf';
  }
};

export default PlanningHeader;
